import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../../lib/api";
import { Card } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Badge } from "../../components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../../components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../../components/ui/dialog";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "../../components/ui/table";
import { Users, Plus, Pencil, Trash2, ArrowLeft, Loader2 } from "lucide-react";
import { toast } from "sonner";

const EMPTY = { nome: "", telefone: "", whatsapp: "", cpf: "", cnh: "", cnh_categoria: "", status: "ativo", observacoes: "" };

export default function Motoristas() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/roteirizacao/motoristas", { params: q ? { q } : {} });
      setItems(data?.items || []);
    } catch { toast.error("Falha ao carregar motoristas"); }
    setLoading(false);
  };
  useEffect(() => { const t = setTimeout(load, 300); return () => clearTimeout(t); /* eslint-disable-next-line */ }, [q]);

  const abrirNovo = () => { setEditing(null); setForm(EMPTY); setOpen(true); };
  const abrirEditar = (m) => { setEditing(m); setForm({ ...EMPTY, ...m }); setOpen(true); };

  const salvar = async () => {
    if (!form.nome?.trim()) { toast.error("Nome é obrigatório"); return; }
    setBusy(true);
    try {
      if (editing?.id) {
        await api.patch(`/roteirizacao/motoristas/${editing.id}`, form);
        toast.success("Motorista atualizado");
      } else {
        await api.post("/roteirizacao/motoristas", form);
        toast.success("Motorista criado");
      }
      setOpen(false); load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falha ao salvar");
    } finally { setBusy(false); }
  };

  const remover = async (m) => {
    if (!window.confirm(`Excluir motorista ${m.nome}?`)) return;
    try {
      await api.delete(`/roteirizacao/motoristas/${m.id}`);
      toast.success("Motorista removido"); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Falha"); }
  };

  return (
    <div className="space-y-4" data-testid="motoristas-page">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3">
          <Link to="/app/roteirizacao"><Button variant="ghost" size="sm"><ArrowLeft size={14} /></Button></Link>
          <Users size={26} style={{ color: "#1B3A6B" }} />
          <h1 className="text-2xl font-bold" style={{ color: "#1B3A6B" }}>Motoristas</h1>
        </div>
        <div className="flex gap-2">
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar..." className="w-60" data-testid="motoristas-busca" />
          <Button onClick={abrirNovo} className="text-white" style={{ background: "#F97316" }} data-testid="btn-novo-motorista">
            <Plus size={14} className="mr-1" /> Novo
          </Button>
        </div>
      </div>

      <Card>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Nome</TableHead>
              <TableHead>Telefone</TableHead>
              <TableHead>WhatsApp</TableHead>
              <TableHead>CNH</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="text-right">Ações</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading && <TableRow><TableCell colSpan={6} className="text-center py-6"><Loader2 className="animate-spin inline" /></TableCell></TableRow>}
            {!loading && items.length === 0 && (
              <TableRow><TableCell colSpan={6} className="text-center py-6 text-muted-foreground">Nenhum motorista cadastrado.</TableCell></TableRow>
            )}
            {!loading && items.map((m) => (
              <TableRow key={m.id} data-testid={`motorista-row-${m.id}`}>
                <TableCell className="font-medium">{m.nome}</TableCell>
                <TableCell className="text-xs">{m.telefone || "—"}</TableCell>
                <TableCell className="text-xs">{m.whatsapp || "—"}</TableCell>
                <TableCell className="text-xs">{m.cnh ? `${m.cnh}${m.cnh_categoria ? ` (${m.cnh_categoria})` : ""}` : "—"}</TableCell>
                <TableCell>
                  <Badge className={m.status === "ativo" ? "bg-emerald-100 text-emerald-800" : "bg-zinc-100 text-zinc-700"}>
                    {m.status}
                  </Badge>
                </TableCell>
                <TableCell className="text-right">
                  <Button size="sm" variant="ghost" onClick={() => abrirEditar(m)} data-testid={`editar-${m.id}`}><Pencil size={14} /></Button>
                  <Button size="sm" variant="ghost" onClick={() => remover(m)} data-testid={`remover-${m.id}`}><Trash2 size={14} className="text-red-600" /></Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="motorista-dialog">
          <DialogHeader>
            <DialogTitle>{editing ? "Editar Motorista" : "Novo Motorista"}</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2">
              <Label>Nome *</Label>
              <Input value={form.nome} onChange={(e) => setForm({ ...form, nome: e.target.value })} data-testid="input-nome" />
            </div>
            <div>
              <Label>Telefone</Label>
              <Input value={form.telefone} onChange={(e) => setForm({ ...form, telefone: e.target.value })} />
            </div>
            <div>
              <Label>WhatsApp</Label>
              <Input value={form.whatsapp} onChange={(e) => setForm({ ...form, whatsapp: e.target.value })} />
            </div>
            <div>
              <Label className="text-xs">CPF (opcional)</Label>
              <Input value={form.cpf} onChange={(e) => setForm({ ...form, cpf: e.target.value })} />
            </div>
            <div>
              <Label className="text-xs">CNH (opcional)</Label>
              <Input value={form.cnh} onChange={(e) => setForm({ ...form, cnh: e.target.value })} />
            </div>
            <div>
              <Label className="text-xs">Categoria CNH</Label>
              <Select value={form.cnh_categoria || "_none"} onValueChange={(v) => setForm({ ...form, cnh_categoria: v === "_none" ? "" : v })}>
                <SelectTrigger><SelectValue placeholder="—" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="_none">—</SelectItem>
                  <SelectItem value="A">A</SelectItem>
                  <SelectItem value="B">B</SelectItem>
                  <SelectItem value="C">C</SelectItem>
                  <SelectItem value="D">D</SelectItem>
                  <SelectItem value="E">E</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Status</Label>
              <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                <SelectTrigger data-testid="sel-status"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="ativo">Ativo</SelectItem>
                  <SelectItem value="inativo">Inativo</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="col-span-2">
              <Label>Observações</Label>
              <Textarea rows={2} value={form.observacoes} onChange={(e) => setForm({ ...form, observacoes: e.target.value })} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
            <Button onClick={salvar} disabled={busy} className="text-white" style={{ background: "#1B3A6B" }} data-testid="btn-salvar-motorista">
              {busy ? <Loader2 className="animate-spin mr-1" size={14} /> : null}
              Salvar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
